import { Link, useNavigate, useParams } from "react-router-dom"
import MainLayout from "../../components/layouts/MainLayout"
import { useEffect, useState } from "react"
import { Alert, Spin, Collapse, Button, message, Tag, Popconfirm, Space } from "antd"
import { PlusCircleOutlined, LeftCircleOutlined, EditOutlined } from '@ant-design/icons';
import { ConditionGeneraleApi } from "../../api/conditionGeneraleApi"
import { DateFormat } from "../../utils/formatDate"
import Swal from "sweetalert2"
import { Auth } from "../../utils/Auth"
import { TYPE_USER_LABELS } from "./constants";
const { Panel } = Collapse;

const statutColors = {
    'BROUILLON': 'default',
    'PUBLIE': 'green',
    'ARCHIVE': 'orange',
}

const DetailsConditionGeneraleView = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [document, setDocument] = useState(null)
    const [loading, setLoading] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [error, setError] = useState(null);
    const abortController = new AbortController()

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await ConditionGeneraleApi.getDocumentDetails(id, abortController.signal);
            setDocument(res.data);
        } catch (err) {
            setError("Impossible de charger les détails du document.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleApiError = async (error) => {
        message.error("Une erreur est survenue");
        const messages = await error?.messages ?? error?.message;

        if (messages instanceof Array)
            return Swal.fire({
                title: messages.join('\n') !== 'Unauthenticated' ? "Erreur détectée" : "Session expirée",
                text: messages.join('\n') !== 'Unauthenticated' ? "Veuillez vous reconncter." : "Votre session a expirée veuillez vous reconnecter",
                icon: "error",
            }).then(() => {
                if (messages.join('\n') === 'Unauthenticated') {
                    Auth.removeSessionToken();
                    return navigate(`/login`);
                }
            });
    }

    const handlePublish = async () => {
        setActionLoading(true);
        try {
            const res = await ConditionGeneraleApi.publishDocument(id, abortController.signal);
            if (!res.success) return message.error(res.message);
            message.success(res.message);
            fetchData();
        } catch (error) {
            await handleApiError(error);
        } finally {
            setActionLoading(false);
        }
    }

    const handleUnpublish = async () => {
        setActionLoading(true);
        try {
            const res = await ConditionGeneraleApi.unpublishDocument(id, abortController.signal);
            if (!res.success) return message.error(res.message);
            message.success(res.message);
            fetchData();
        } catch (error) {
            await handleApiError(error);
        } finally {
            setActionLoading(false);
        }
    }

    const handleArchive = async () => {
        setActionLoading(true);
        try {
            const res = await ConditionGeneraleApi.archiveDocument(id, abortController.signal);
            if (!res.success) return message.error(res.message);
            message.success(res.message);
            fetchData();
        } catch (error) {
            await handleApiError(error);
        } finally {
            setActionLoading(false);
        }
    }

    const handleDelete = async () => {
        setActionLoading(true);
        try {
            const res = await ConditionGeneraleApi.deleteDocument(id, abortController.signal);
            if (!res.success) {
                setActionLoading(false);
                return message.error(res.message);
            }
            message.success(res.message);
            navigate('/conditions-generales');
        } catch (error) {
            setActionLoading(false);
            await handleApiError(error);
        }
    }

    const handleTypeUserRender = (typeUser) => TYPE_USER_LABELS[typeUser] ?? '';

    return <MainLayout>
        <div className="container mx-auto p-6">
            <div className="flex justify-between">
                <h1 className="text-3xl font-semibold mb-6">Détails du document {document?.title}</h1>
                <Link to={`/conditions-generales`}><LeftCircleOutlined /> Retour  </Link>
            </div>

            {loading && <Spin size="large" />}
            {error && <Alert message={error} type="error" />}
            {!loading && !error && document && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <Collapse defaultActiveKey={['1']} expandIconPosition="start">
                        <Panel header="Informations générales" key="1" extra={<PlusCircleOutlined />}>
                            <div className="space-y-4">
                                <p><strong>Type :</strong> {document?.type}</p>
                                <p><strong>Titre :</strong> {document?.title}</p>
                                <p><strong>Public cible :</strong> {handleTypeUserRender(document?.type_user)}</p>
                                <p><strong>Version :</strong> {document?.version}</p>
                                <p><strong>Statut :</strong> {document?.statut ? <Tag color={statutColors[document.statut] || 'default'}>{document.statut}</Tag> : 'Non renseigné'}</p>
                                <p><strong>Auteur de la publication :</strong> {document?.published_by ? `${document.published_by.name ?? ''} ${document.published_by.lastname ?? ''}` : "Non publié"}</p>
                                <p><strong>Date de publication :</strong> {document?.published_at ? DateFormat.formatDate(document.published_at) : "Non publié"}</p>
                                <p><strong>Date de création :</strong> {DateFormat.formatDate(document?.created_at)}</p>
                                <p><strong>Dernière modification :</strong> {DateFormat.formatDate(document?.updated_at)}</p>
                            </div>
                        </Panel>
                    </Collapse>

                    <Collapse defaultActiveKey={['2']} expandIconPosition="start" className="mt-4">
                        <Panel header="Contenu du document" key="2" extra={<PlusCircleOutlined />}>
                            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: document?.content }}></div>
                        </Panel>
                    </Collapse>

                    <div className="mt-6">
                        <Space wrap>
                            <Button icon={<EditOutlined />} onClick={() => navigate(`/conditions-generales/${id}/modifier`)}>
                                Modifier
                            </Button>

                            {document?.statut !== 'PUBLIE' && (
                                <Button type="primary" loading={actionLoading} onClick={handlePublish}>
                                    Publier
                                </Button>
                            )}

                            {document?.statut === 'PUBLIE' && (
                                <Button loading={actionLoading} onClick={handleUnpublish}>
                                    Dépublier
                                </Button>
                            )}

                            {document?.statut !== 'ARCHIVE' && (
                                <Button loading={actionLoading} onClick={handleArchive}>
                                    Archiver
                                </Button>
                            )}

                            <Popconfirm
                                title="Supprimer ce document ?"
                                description={document?.statut === 'PUBLIE' ? "Ce document est publié, il doit d'abord être dépublié." : undefined}
                                onConfirm={handleDelete}
                                okText="Oui"
                                cancelText="Non"
                            >
                                <Button danger loading={actionLoading}>
                                    Supprimer
                                </Button>
                            </Popconfirm>
                        </Space>
                    </div>
                </div>
            )}
        </div>
    </MainLayout>
}
export default DetailsConditionGeneraleView
